import React, { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Target, FileText, Download } from 'lucide-react';
import axios from 'axios';
import './App.css';

function App() {
  const [financialGoal, setFinancialGoal] = useState('');
  const [generatedPlan, setGeneratedPlan] = useState('');
  const [htmlUrl, setHtmlUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!financialGoal.trim()) {
      toast.error('Please enter your financial goal');
      return;
    }

    setIsLoading(true);
    
    // Reset previous plan data
    setGeneratedPlan('');
    setHtmlUrl('');
    
    try {
      const response = await axios.post('/api/generate-plan', {
        financialGoal
      });

      if (response.data.success) {
        setGeneratedPlan(response.data.plan);
        setHtmlUrl(response.data.htmlUrl || '');
        toast.success('Financial plan generated successfully!');
      } else {
        toast.error('Failed to generate plan');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.error || 'Failed to generate financial plan');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPlan = () => {
    if (!generatedPlan) return;
    
    // If we have an S3 HTML URL, try to download from S3
    if (htmlUrl) {
      // Try to fetch and download the file
      fetch(htmlUrl)
        .then(response => response.blob())
        .then(blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'financial-plan.html';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success('Financial plan downloaded from S3!');
        })
        .catch(error => {
          console.error('S3 download failed, falling back to local:', error);
          // Fallback to local download
          const blob = new Blob([generatedPlan], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'financial-plan.html';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success('Financial plan downloaded!');
        });
    } else {
      // Local download
      const blob = new Blob([generatedPlan], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'financial-plan.html';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Financial plan downloaded!');
    }
  };

  const printPlan = () => {
    if (!generatedPlan) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Financial Plan - Print</title>
          <style>
            @media print {
              body { 
                font-family: Arial, sans-serif; 
                margin: 0.5in; 
                padding: 0; 
                line-height: 1.4; 
                background: white; 
                color: black;
                font-size: 12pt;
              }
              h1, h2, h3 { 
                color: #003366; 
                margin-top: 1.5rem; 
                margin-bottom: 0.5rem;
                page-break-after: avoid;
              }
              h1 { font-size: 18pt; }
              h2 { font-size: 14pt; }
              h3 { font-size: 12pt; }
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin: 1rem 0; 
                page-break-inside: avoid;
              }
              th, td { 
                border: 1px solid #333; 
                padding: 0.4rem; 
                text-align: left; 
                font-size: 10pt;
              }
              th { 
                background: #f0f4f8; 
                font-weight: bold;
              }
              caption { 
                caption-side: top; 
                font-weight: bold; 
                margin-bottom: 0.4rem; 
                font-size: 11pt;
              }
              ul { 
                margin-left: 1.2rem; 
                page-break-inside: avoid;
              }
              li { margin-bottom: 0.3rem; }
              .note { 
                font-size: 9pt; 
                color: #666; 
                font-style: italic;
              }
            }
          </style>
        </head>
        <body>
          ${generatedPlan}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div className="App">
      <ToastContainer position="top-right" />
      
      <div className="container">
        <header className="header">
                      <div className="header-content">
              <h1>PlanBolt AI</h1>
              <p>Start where you are. Plan where you're going</p>
            </div>
        </header>

        <div className="main-content">
          <div className="card">
            <div className="card-header">
              <Target size={24} />
              <h2>What Are Your Financial Goal(s)?</h2>
            </div>
            
            <form onSubmit={handleSubmit} className="goal-form">
              <div className="form-group">
                <label htmlFor="financialGoal">Describe your financial situation and your goal(s) in 2-3 sentences:</label>
                <textarea
                  id="financialGoal"
                  className="input"
                  value={financialGoal}
                  onChange={(e) => setFinancialGoal(e.target.value)}
                  placeholder="e.g., I want to save $500,000 for retirement by age 65, or I want to buy a house worth $300,000 in 5 years..."
                  rows={4}
                  required
                />
              </div>

              <button 
                type="submit" 
                className="btn generate-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="spinner-small"></div>
                    Generating Plan...
                  </>
                ) : (
                  <>
                    <FileText size={20} />
                    Generate Financial Plan
                  </>
                )}
              </button>
            </form>
          </div>

          {generatedPlan && (
            <div className="card">
              <div className="card-header">
                <FileText size={24} />
                <h2>Your Financial Plan</h2>
                <div className="plan-actions">
                  <button onClick={downloadPlan} className="btn btn-secondary">
                    <Download size={16} />
                    {htmlUrl ? 'Download from S3' : 'Download'}
                  </button>
                  <button onClick={printPlan} className="btn btn-secondary">
                    Print
                  </button>
                </div>
              </div>
              
              <div 
                className="plan-content"
                dangerouslySetInnerHTML={{ __html: generatedPlan }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App; 